import { ModulePublishingState } from '../../@types/module-publishing-state';
import { Module } from '../../api/module';
import Identity from '../security/identity';
import Role from '../security/role';
import {
  FEATURE_ACCESS,
  FeatureAccess,
  MODULE_LICENSE,
  ModuleAccess,
  ModuleAccessInput,
  ModuleLicense,
} from './access-types';
import { withBaoRules } from './bao-module-access';

export * from './access-types';

export function getModuleLicense(
  { licenses, isStudentModule }: Pick<Module, 'licenses' | 'isStudentModule'>,
  isTeacherAlike: boolean,
): ModuleLicense {
  const hasDocumentLicense = licenses.hasDocumentLicenseForTeacher || licenses.hasDocumentLicenseForStudent;

  if (isTeacherAlike && isStudentModule) {
    return hasDocumentLicense ? MODULE_LICENSE.STUDENT_LICENSE : MODULE_LICENSE.STUDENT_NO_DOCUMENT;
  }
  if (licenses.hasDemoProductLicenseForTeacher && !hasDocumentLicense) return MODULE_LICENSE.DEMO;

  if (!isTeacherAlike) {
    return licenses.hasDocumentLicenseForStudent ? MODULE_LICENSE.FULL : MODULE_LICENSE.STUDENT_NO_DOCUMENT;
  }

  if (licenses.hasDemoProductLicenseForTeacher) return MODULE_LICENSE.FULL;
  if (!licenses.hasDocumentLicenseForTeacher) return MODULE_LICENSE.TEACHER_NO_DOCUMENT;
  if (!licenses.hasDigitalManualLicenseForTeacher) return MODULE_LICENSE.TEACHER_NO_DIGITAL_MANUAL;

  return MODULE_LICENSE.FULL;
}

type LicensedModuleLicense =
  | typeof MODULE_LICENSE.FULL
  | typeof MODULE_LICENSE.DEMO
  | typeof MODULE_LICENSE.STUDENT_LICENSE;

export function isLicensed(license: ModuleLicense | undefined): license is LicensedModuleLicense {
  return (
    license === MODULE_LICENSE.FULL || license === MODULE_LICENSE.DEMO || license === MODULE_LICENSE.STUDENT_LICENSE
  );
}

export function canOpenModule(identity: Identity, publishingState: ModulePublishingState, moduleId: string): boolean {
  if (
    identity.hasAnyRole([Role.SYSADMIN, Role.PRODUCTION, Role.EDITOR, Role.PUBLISHER]) ||
    identity.author_modules.includes(moduleId)
  ) {
    return true;
  }

  return !(publishingState === 'preview' && !identity.roles.includes(Role.SALES));
}

export function getModuleAccess(input: ModuleAccessInput): ModuleAccess {
  const access = getDefaultModuleAccess(input);

  return input.studioGroup === 'BAO' ? withBaoRules(access, input) : access;
}

function getFeatureAccess(licensed: boolean | undefined): FeatureAccess {
  return licensed ? FEATURE_ACCESS.ALLOWED : FEATURE_ACCESS.LOCKED;
}

function getDefaultModuleAccess({ module, identity }: ModuleAccessInput): ModuleAccess {
  const { isTeacherAlike } = identity;
  const licenses = module?.licenses;
  const license = module ? getModuleLicense(module, isTeacherAlike) : undefined;
  const isTeacherOnStudentModule = isTeacherAlike && Boolean(module?.isStudentModule);
  const actsAs = isTeacherAlike && !isTeacherOnStudentModule ? 'teacher' : 'student';
  const actsAsTeacher = actsAs === 'teacher';

  function getAssignmentsAccess(): FeatureAccess {
    if (actsAsTeacher) return getFeatureAccess(licenses?.hasDigitalManualLicenseForTeacher);
    // Students follow up their own assignments, a teacher on a student module has none.
    return isTeacherAlike ? FEATURE_ACCESS.HIDDEN : FEATURE_ACCESS.ALLOWED;
  }

  return {
    license,
    actsAs,
    canOpen: module ? canOpenModule(identity, module.publishingState, module.id) : false,
    appsAccess: getFeatureAccess(
      license === MODULE_LICENSE.FULL ||
        license === MODULE_LICENSE.DEMO ||
        license === MODULE_LICENSE.STUDENT_LICENSE ||
        license === MODULE_LICENSE.TEACHER_NO_DIGITAL_MANUAL,
    ),
    assignmentsAccess: getAssignmentsAccess(),
    resultsAccess: actsAsTeacher ? FEATURE_ACCESS.ALLOWED : FEATURE_ACCESS.HIDDEN,
    materialAccess: actsAsTeacher ? getFeatureAccess(licenses?.hasDocumentLicenseForTeacher) : FEATURE_ACCESS.HIDDEN,
    canShare: !isTeacherOnStudentModule,
    canCreatePresentation: actsAsTeacher && Boolean(licenses?.hasDocumentLicenseForTeacher),
  };
}
